/**
 * Mock GPT-style request analysis. Replace with OpenAI API calls later.
 */

export const ANALYSIS_MODES = {
  standard: "standard",
  detailed: "detailed",
};

function clip(s, max) {
  const t = (s || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

export function buildMockSuccessSummary(request, mode, note) {
  const service = clip(request.serviceTitle, 80) || "General inquiry";
  const reqLine = clip(request.requirements, 200);
  const desc = clip(request.description, 320);
  const who =
    request.requesterType === "company"
      ? `${clip(request.name, 60)} (${clip(request.companyName, 80) || "company"})`
      : clip(request.name, 100);

  if (mode === ANALYSIS_MODES.detailed) {
    const noteBlock = note.trim()
      ? `\n\nTeam instructions for this pass:\n${note.trim()}`
      : "";
    return (
      `**Executive read**\n` +
      `Contact: ${who}. Service line: **${service}**.\n\n` +
      `**What they are asking for**\n` +
      `${reqLine || "—"}\n\n` +
      `**Context**\n` +
      `${desc || "—"}\n\n` +
      `**Suggested next steps**\n` +
      `1. Confirm scope, timeline, and environments (staging vs production).\n` +
      `2. Share a short discovery checklist and security expectations.\n` +
      `3. Propose a follow-up call with engineering + decision-maker.\n\n` +
      `**Risks / gaps**\n` +
      `- Budget and success criteria are not stated — ask explicitly.\n` +
      `- Clarify data residency and compliance if cloud is involved.${noteBlock}`
    );
  }

  return (
    `**Summary:** ${who} is asking about **${service}**. ` +
      `Key need: ${reqLine || "See description."} ` +
      `**Next step:** reply with a concise scope checklist and offer a discovery call.`
  );
}

export function buildMockErrorMessage(attemptIndex) {
  const errors = [
    "OpenAI API returned 429 (rate limit). Retry shortly or switch model.",
    "The model failed to complete: context length exceeded after tokenization.",
    "Network timeout calling api.openai.com — no response body.",
  ];
  return errors[attemptIndex % errors.length];
}

/**
 * Decide mock outcome for a manual or automatic run.
 * @param {object} request - normalized row
 * @param {{ mode: string, note: string, auto?: boolean, forceFail?: boolean }} opts
 */
export function computeMockAnalysisOutcome(request, opts) {
  if (opts.forceFail) {
    return {
      ok: false,
      summary: request.aiAnalysis?.summary || "",
      error: buildMockErrorMessage(request.aiAnalysis?.attempts || 0),
    };
  }
  if (opts.auto) {
    return {
      ok: true,
      summary: buildMockSuccessSummary(request, ANALYSIS_MODES.standard, ""),
      error: "",
    };
  }
  if (opts.mode === ANALYSIS_MODES.detailed) {
    return {
      ok: true,
      summary: buildMockSuccessSummary(request, ANALYSIS_MODES.detailed, opts.note || ""),
      error: "",
    };
  }
  // standard manual re-run: succeed with refreshed standard summary
  return {
    ok: true,
    summary: buildMockSuccessSummary(request, ANALYSIS_MODES.standard, opts.note || ""),
    error: "",
  };
}
